"use client";

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertCircle,
  Gauge,
  MapPin,
  TrendingUp,
} from "lucide-react";
import API_URL from "../../../src/Config";
import "./allSection.css";

const INITIAL_FILTERS = {
  location_id: "",
  start_date: "",
  end_date: "",
};

const INITIAL_SUMMARY = {
  latest_evaluation: null,
  average_score: {
    value: null,
    evaluations_counted: 0,
    best: null,
    worst: null,
    label: "",
  },
  pending_approval: { count: 0, label: "" },
  coverage: {
    locations_evaluated: 0,
    locations_total: 0,
    percentage: null,
  },
};

const INITIAL_CHARTS = {
  score_by_location: [],
  score_trend: [],
  weakest_categories: [],
};

const STATUS_COLORS = {
  Approved: "hsl(142, 71%, 45%)",
  Draft: "hsl(38, 92%, 50%)",
  Submitted: "hsl(217, 91%, 60%)",
  Reviewed: "hsl(260, 84%, 60%)",
};

const numberOrZero = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === "") return "NA";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "NA";
  return numericValue.toFixed(digits);
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "NA";
  return `${numericValue.toFixed(1)}%`;
};

const formatDateLabel = (value) => {
  if (!value) return "NA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const buildErrorMessage = (filters) => {
  if ((filters.start_date && !filters.end_date) || (!filters.start_date && filters.end_date)) {
    return "Select both start and end dates.";
  }
  if (
    filters.start_date &&
    filters.end_date &&
    new Date(filters.end_date) < new Date(filters.start_date)
  ) {
    return "End date cannot be before start date.";
  }
  return "";
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload || {};

  return (
    <div className="newdashboard-tooltip">
      <p className="newdashboard-tooltip-label">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey || entry.name} style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
      {data.status && <p>Status: {data.status}</p>}
      {data.region && <p>Region: {data.region}</p>}
      {data.zone && <p>Zone: {data.zone}</p>}
      {data.evaluationType && <p>Type: {data.evaluationType}</p>}
      {data.evaluatedOn && <p>Evaluated On: {data.evaluatedOn}</p>}
      {data.categoryName && <p>{data.categoryName}</p>}
      {data.itemsScored !== undefined && <p>Items Scored: {data.itemsScored}</p>}
      {data.exceedsMaximum && <p>Check Data: Obtained exceeds maximum</p>}
    </div>
  );
};

export function MIIndexSection() {
  const [summary, setSummary] = useState(INITIAL_SUMMARY);
  const [charts, setCharts] = useState(INITIAL_CHARTS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchLocations = useCallback(async () => {
    try {
      setFilterLoading(true);
      const authKey = localStorage.getItem("authKey") || "";
      const response = await axios.get(`${API_URL}/api/mi/locations`, {
        headers: { Authorization: authKey },
        params: { page: 1, per_page: 1000 },
      });
      setLocations(response.data?.locations || []);
    } catch (error) {
      console.error("Error fetching MI locations:", error);
    } finally {
      setFilterLoading(false);
    }
  }, []);

  const fetchMIData = useCallback(async (currentFilters = INITIAL_FILTERS) => {
    const validationMessage = buildErrorMessage(currentFilters);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const authKey = localStorage.getItem("authKey") || "";
      const params = new URLSearchParams();

      if (currentFilters.location_id) {
        params.append("location_id", currentFilters.location_id);
      }
      if (currentFilters.start_date && currentFilters.end_date) {
        params.append("start_date", currentFilters.start_date);
        params.append("end_date", currentFilters.end_date);
      }

      const response = await axios.get(
        `${API_URL}/api/kpi/mi-index?${params.toString()}`,
        { headers: { Authorization: authKey } }
      );

      setSummary({ ...INITIAL_SUMMARY, ...(response.data?.summary || {}) });
      setCharts({ ...INITIAL_CHARTS, ...(response.data?.charts || {}) });
    } catch (error) {
      console.error("Error fetching MI index KPIs:", error);
      setErrorMessage(
        error?.response?.data?.message || "Failed to load MI index KPIs."
      );
      setSummary(INITIAL_SUMMARY);
      setCharts(INITIAL_CHARTS);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFilterChange = (key, value) => {
    const nextFilters = { ...filters, [key]: value };

    if (key === "start_date" && !value) {
      nextFilters.end_date = "";
    }

    setFilters(nextFilters);
    const validationMessage = buildErrorMessage(nextFilters);
    setErrorMessage(validationMessage);

    if (!validationMessage) {
      fetchMIData(nextFilters);
    }
  };

  const handleResetFilters = () => {
    setFilters(INITIAL_FILTERS);
    fetchMIData(INITIAL_FILTERS);
  };

  useEffect(() => {
    fetchLocations();
    fetchMIData(INITIAL_FILTERS);
  }, [fetchLocations, fetchMIData]);

  const latestEvaluation = summary.latest_evaluation || {};

  const scoreByLocationData = (charts.score_by_location || [])
    .slice(0, 10)
    .map((item) => ({
      location: item.location || "NA",
      Score: numberOrZero(item.score),
      status: item.status || "NA",
      region: item.region || "",
      zone: item.zone || "",
      evaluationType: item.evaluation_type || "",
      evaluatedOn: formatDateLabel(item.evaluated_on),
    }));

  const trendData = (charts.score_trend || []).map((item) => ({
    date: formatDateLabel(item.evaluated_on),
    Score: numberOrZero(item.score),
    evaluationType: item.evaluation_type || "",
    evaluatedOn: formatDateLabel(item.evaluated_on),
  }));

  const weakestCategoriesData = (charts.weakest_categories || []).map((item) => ({
    category: item.category_code || item.category || "NA",
    categoryName: item.category || "NA",
    Percentage: numberOrZero(item.percentage),
    Obtained: numberOrZero(item.obtained),
    Maximum: numberOrZero(item.maximum),
    itemsScored: numberOrZero(item.items_scored),
    exceedsMaximum: Boolean(item.exceeds_maximum),
  }));

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="newdashboard-index-section">
      <div className="newdashboard-index-header">
        <div>
          <h2 className="newdashboard-section-title">MI Index</h2>
          <p className="newdashboard-index-subtitle">
            Monitor approved MI scores, pending evaluations, and location coverage
          </p>
        </div>

        <div className="newdashboard-index-filters">
          <div className="newdashboard-filter-group">
            <label className="newdashboard-filter-label">Location</label>
            <select
              className="newdashboard-select"
              value={filters.location_id}
              onChange={(event) =>
                handleFilterChange("location_id", event.target.value)
              }
              disabled={filterLoading}
            >
              <option value="">All Locations</option>
              {locations.map((location) => (
                <option
                  key={location.location_id}
                  value={location.location_id}
                >
                  {location.location_name}
                </option>
              ))}
            </select>
          </div>

          <div className="newdashboard-filter-group">
            <label className="newdashboard-filter-label">Start Date</label>
            <input
              className="newdashboard-date-input"
              type="date"
              value={filters.start_date}
              onChange={(event) =>
                handleFilterChange("start_date", event.target.value)
              }
            />
          </div>

          <div className="newdashboard-filter-group">
            <label className="newdashboard-filter-label">End Date</label>
            <span
              className="newdashboard-index-tooltip-wrap"
              title={!filters.start_date ? "Select start date first" : ""}
            >
              <input
                className="newdashboard-date-input"
                type="date"
                value={filters.end_date}
                min={filters.start_date || undefined}
                disabled={!filters.start_date}
                onChange={(event) =>
                  handleFilterChange("end_date", event.target.value)
                }
              />
            </span>
          </div>

          <div className="newdashboard-filter-actions">
            <button
              className="newdashboard-btn newdashboard-btn-outline"
              type="button"
              onClick={handleResetFilters}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="newdashboard-index-error">{errorMessage}</div>
      )}

      <div className="newdashboard-kpi-grid">
        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Latest MI Score</h3>
            <Gauge className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value newdashboard-text-primary">
              {formatNumber(latestEvaluation.score)}
            </div>
            <p className="newdashboard-card-subtitle">
              {latestEvaluation.location
                ? `${latestEvaluation.location} | ${latestEvaluation.evaluation_type || "NA"} | ${formatDateLabel(latestEvaluation.evaluated_on)}`
                : "No approved evaluation"}
            </p>
            {latestEvaluation.marks && (
              <p className="newdashboard-card-subtitle">
                {formatNumber(latestEvaluation.marks.obtained)} /{" "}
                {formatNumber(latestEvaluation.marks.maximum)} marks,{" "}
                {formatNumber(latestEvaluation.marks.negative)} negative
              </p>
            )}
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Average Score</h3>
            <TrendingUp className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value newdashboard-text-success">
              {formatNumber(summary.average_score?.value)}
            </div>
            <p className="newdashboard-card-subtitle">
              {summary.average_score?.evaluations_counted || 0} approved
              evaluations
            </p>
            <p className="newdashboard-card-subtitle">
              Best {formatNumber(summary.average_score?.best)}, worst{" "}
              {formatNumber(summary.average_score?.worst)}
            </p>
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Pending Approval</h3>
            <AlertCircle className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value newdashboard-text-danger">
              {summary.pending_approval?.count || 0}
            </div>
            <p className="newdashboard-card-subtitle">
              {summary.pending_approval?.label || "Evaluations still in draft"}
            </p>
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header">
            <h3 className="newdashboard-card-title">Coverage</h3>
            <MapPin className="newdashboard-card-icon" />
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-card-value-medium">
              {formatPercent(summary.coverage?.percentage)}
            </div>
            <p className="newdashboard-card-subtitle">
              {summary.coverage?.locations_evaluated || 0} of{" "}
              {summary.coverage?.locations_total || 0} locations
            </p>
          </div>
        </div>
      </div>

      <div className="newdashboard-charts-grid">
        <div className="newdashboard-card">
          <div className="newdashboard-card-header-full">
            <h3 className="newdashboard-card-title">Score By Location</h3>
            <p className="newdashboard-card-description">
              Latest location scores returned for the selected filters
            </p>
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-chart-container">
              {scoreByLocationData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={scoreByLocationData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="location" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="Score" name="MI Score">
                      {scoreByLocationData.map((entry) => (
                        <Cell
                          key={`${entry.location}-${entry.status}`}
                          fill={STATUS_COLORS[entry.status] || "hsl(217, 91%, 60%)"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </div>
          </div>
        </div>

        <div className="newdashboard-card">
          <div className="newdashboard-card-header-full">
            <h3 className="newdashboard-card-title">Score Trend</h3>
            <p className="newdashboard-card-description">
              Approved MI scores over evaluation dates
            </p>
          </div>
          <div className="newdashboard-card-content">
            <div className="newdashboard-chart-container">
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Score"
                      stroke="hsl(142, 71%, 45%)"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="newdashboard-card">
        <div className="newdashboard-card-header-full">
          <h3 className="newdashboard-card-title">Weakest Categories</h3>
            <p className="newdashboard-card-description">
            Category marks compared with maximum marks and score percentage
          </p>
        </div>
        <div className="newdashboard-card-content">
          <div className="newdashboard-chart-container">
            {weakestCategoriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={weakestCategoriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="category" stroke="#6b7280" fontSize={12} />
                  <YAxis yAxisId="marks" stroke="#6b7280" fontSize={12} />
                  <YAxis
                    yAxisId="percent"
                    orientation="right"
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    yAxisId="marks"
                    dataKey="Obtained"
                    fill="hsl(217, 91%, 60%)"
                  />
                  <Bar
                    yAxisId="marks"
                    dataKey="Maximum"
                    fill="hsl(215, 16%, 47%)"
                  />
                  <Line
                    yAxisId="percent"
                    type="monotone"
                    dataKey="Percentage"
                    stroke="hsl(38, 92%, 50%)"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
